import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Share2 } from 'lucide-react';
import { toast } from 'sonner';

interface MobileStickyHeaderProps {
  name: string;
  city: string;
  price: string;
  thumbUrl?: string;
  triggerRef: React.RefObject<HTMLElement>;
  onVisibilityChange?: (visible: boolean) => void;
}

export function MobileStickyHeader({ name, city, price, thumbUrl, triggerRef, onVisibilityChange }: MobileStickyHeaderProps) {
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!triggerRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const v = !entry.isIntersecting;
        setVisible(v);
        onVisibilityChange?.(v);
      },
      { threshold: 0, rootMargin: '-50px 0px 0px 0px' }
    );
    observer.observe(triggerRef.current);
    return () => observer.disconnect();
  }, [triggerRef, onVisibilityChange]);

  if (!visible) return null;

  return (
    <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-background border-b shadow-sm animate-in slide-in-from-top duration-200">
      <div className="flex items-center gap-3 px-3 py-2.5">
        <button onClick={() => navigate(-1)} className="p-1.5 -ml-1 rounded-full hover:bg-muted shrink-0">
          <X className="h-5 w-5" />
        </button>
        {thumbUrl && (
          <div className="h-10 w-10 rounded-md overflow-hidden bg-muted shrink-0">
            <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate leading-tight">{name}</div>
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground truncate">
            <span className="truncate">{city}</span>
            <span>•</span>
            <span className="font-semibold text-primary truncate">{price}</span>
          </div>
        </div>
        <button
          onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success('Link copied!'); }}
          className="p-1.5 rounded-full hover:bg-muted shrink-0"
        >
          <Share2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
